import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AdminContext";
import Swal from "sweetalert2";
import {
  useLazyGetNewslettersQuery,
  useDeleteNewsletterMutation,
  useUpdateNewsletterMutation
} from "../api/businessApi";

// Simple Rich Text Editor Component (React 19 Compatible)
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const textareaRef = React.useRef(null);

  const insertAtCursor = (text) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const textBefore = textarea.value.substring(0, start);
    const textAfter = textarea.value.substring(end);

    textarea.value = textBefore + text + textAfter;
    textarea.selectionStart = textarea.selectionEnd = start + text.length;
    textarea.focus();
    onChange(textarea.value);
  };

  const wrapSelection = (before, after = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const textBefore = textarea.value.substring(0, start);
    const textAfter = textarea.value.substring(end);

    textarea.value = textBefore + before + selectedText + after + textAfter;
    textarea.selectionStart = start + before.length;
    textarea.selectionEnd = start + before.length + selectedText.length;
    textarea.focus();
    onChange(textarea.value);
  };

  const addBold = () => wrapSelection('<strong>', '</strong>');
  const addItalic = () => wrapSelection('<em>', '</em>');
  const addUnderline = () => wrapSelection('<u>', '</u>');
  const addStrike = () => wrapSelection('<s>', '</s>');
  const addBullet = () => insertAtCursor('<ul><li></li></ul>');
  const addNumber = () => insertAtCursor('<ol><li></li></ol>');
  const addLink = () => {
    const url = prompt('Enter URL:');
    if (url) wrapSelection(`<a href="${url}">`, '</a>');
  };

  const addHeading = (level) => wrapSelection(`<h${level}>`, `</h${level}>`);
  const clearFormat = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const textBefore = textarea.value.substring(0, start);
    const textAfter = textarea.value.substring(end);

    // Remove HTML tags (simple approach)
    const plainText = selectedText.replace(/<[^>]*>/g, '');
    textarea.value = textBefore + plainText + textAfter;
    textarea.selectionStart = textarea.selectionEnd = start + plainText.length;
    textarea.focus();
    onChange(textarea.value);
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      {/* Editor Toolbar */}
      <div className="bg-gray-50 border-b border-gray-300 p-2 flex flex-wrap gap-1">
        {/* Text Formatting */}
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            addBold();
          }}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100 font-bold text-sm"
          title="Bold"
        >
          B
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            addItalic();
          }}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100 italic text-sm"
          title="Italic"
        >
          I
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            addUnderline();
          }}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100 underline text-sm"
          title="Underline"
        >
          U
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            addStrike();
          }}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100 line-through text-sm"
          title="Strikethrough"
        >
          S
        </button>

        <div className="w-px h-6 bg-gray-300"></div>

        {/* Headings */}
        <select
          onChange={(e) => {
            if (e.target.value) {
              addHeading(parseInt(e.target.value));
              e.target.value = '';
            }
          }}
          className="px-2 py-1 bg-white border border-gray-300 rounded text-sm"
          title="Heading"
        >
          <option value="">Heading</option>
          <option value="1">H1</option>
          <option value="2">H2</option>
          <option value="3">H3</option>
          <option value="4">H4</option>
        </select>

        <div className="w-px h-6 bg-gray-300"></div>

        {/* Lists */}
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            addBullet();
          }}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100 text-sm"
          title="Bullet List"
        >
          •
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            addNumber();
          }}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100 text-sm"
          title="Numbered List"
        >
          1.
        </button>

        <div className="w-px h-6 bg-gray-300"></div>

        {/* Other */}
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            addLink();
          }}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100 text-sm"
          title="Insert Link"
        >
          Link
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            clearFormat();
          }}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100 text-sm"
          title="Clear Formatting"
        >
          Clear
        </button>

        <div className="flex-1"></div>

        <button
          type="button"
          onClick={() => setIsPreviewMode(!isPreviewMode)}
          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
        >
          {isPreviewMode ? 'Edit' : 'Preview'}
        </button>
      </div>

      {/* Editor Content */}
      {isPreviewMode ? (
        <div className="p-4 min-h-64 bg-white prose max-w-none">
          <div dangerouslySetInnerHTML={{ __html: value }} />
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full p-4 min-h-64 bg-white focus:outline-none resize-none"
          style={{
            minHeight: '300px',
            lineHeight: '1.5',
            fontSize: '14px',
            fontFamily: 'inherit'
          }}
        />
      )}
    </div>
  );
};

const NewsletterManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingNewsletter, setEditingNewsletter] = useState(null);
  const fileInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newImages = [];

    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const imageUrl = event.target.result;
          newImages.push(imageUrl);

          // Update form data with new images array
          setFormData(prev => ({
            ...prev,
            images: [...(prev.images || []), ...newImages]
          }));
        };
        reader.readAsDataURL(file);
      }
    });

    // Reset file input
    e.target.value = '';
  };

  const [formData, setFormData] = useState({
    newsletter_id: "",
    title: "",
    month: "",
    year: "",
    excerpt: "",
    content: "",
    images: [],
    author: "Admin Team",
    read_time: "5 min read",
    featured: false,
    category: "Business",
    publish_date: "",
    status: "draft"
  });

  // RTK Query hooks
  const [getNewsletters, { data: newslettersData, isLoading: loading, isSuccess, isFetching, error: fetchError }] = useLazyGetNewslettersQuery();
  const [updateNewsletter, { isLoading: isUpdating }] = useUpdateNewsletterMutation();
  const [deleteNewsletter, { isLoading: isDeleting }] = useDeleteNewsletterMutation();

  // Local state for newsletters
  const [newsletters, setNewsletters] = useState([]);

  // Fetch newsletters on component mount
  useEffect(() => {
    fetchNewsletters();
  }, []);

  // Update local state when data is fetched
  useEffect(() => {
    if (!loading && !isFetching && isSuccess && newslettersData) {
      // Handle both direct array and nested object structure
      if (Array.isArray(newslettersData)) {
        setNewsletters(newslettersData);
      } else if (newslettersData.newsletters && Array.isArray(newslettersData.newsletters)) {
        setNewsletters(newslettersData.newsletters);
        // Update pagination state from API response
        if (newslettersData.pagination) {
          setCurrentPage(newslettersData.pagination.currentPage);
          setItemsPerPage(newslettersData.pagination.itemsPerPage);
        }
      } else {
        console.warn("Unexpected data structure:", newslettersData);
        setNewsletters([]);
      }
    }
  }, [newslettersData, loading, isFetching, isSuccess]);

  // Search and filter logic (client-side for now, but could be moved to server-side)
  const filteredNewsletters = newsletters.filter((newsletter) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      newsletter.title?.toLowerCase().includes(searchLower) ||
      newsletter.month?.toLowerCase().includes(searchLower) ||
      newsletter.year?.toLowerCase().includes(searchLower) ||
      newsletter.excerpt?.toLowerCase().includes(searchLower) ||
      newsletter.author?.toLowerCase().includes(searchLower) ||
      newsletter.category?.toLowerCase().includes(searchLower) ||
      newsletter.status?.toLowerCase().includes(searchLower)
    );
  });

  // Use API pagination info if available, otherwise fall back to client-side calculation
  const apiPagination = newslettersData?.pagination;
  const totalItems = apiPagination?.totalItems || filteredNewsletters.length;
  const totalPages = apiPagination?.totalPages || Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(startIndex + itemsPerPage - 1, totalItems);
  
  // Use current newsletters (already paginated from API) or filtered results
  const currentNewsletters = searchTerm ? filteredNewsletters : newsletters;

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Fetch new data for the requested page
    getNewsletters({ page, limit: itemsPerPage });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
    // Fetch new data with updated items per page
    getNewsletters({ page: 1, limit: newItemsPerPage });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
    // Refetch first page when clearing search
    getNewsletters({ page: 1, limit: itemsPerPage });
  };

  const fetchNewsletters = async () => {
    try {
      getNewsletters({ page: currentPage, limit: itemsPerPage });
    } catch (err) {
      console.error("Error fetching newsletters:", err);
    }
  };
  const handleCreateNewsletter = () => {
    navigate('/admin/blog-editor');
  };

  const handleUpdateNewsletter = async (e) => {
    e.preventDefault();
    try {
      await updateNewsletter({ id: editingNewsletter.id, ...formData }).unwrap();
      setEditingNewsletter(null);
      resetForm();
      fetchNewsletters();
    } catch (err) {
      console.error("Error updating newsletter:", err);
    }
  };

  const handleDeleteNewsletter = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this newsletter!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await deleteNewsletter(id).unwrap();
        fetchNewsletters();
        Swal.fire(
          'Deleted!',
          'Newsletter has been deleted successfully.',
          'success'
        );
      } catch (err) {
        console.error("Error deleting newsletter:", err);
        Swal.fire(
          'Error!',
          'Failed to delete newsletter. Please try again.',
          'error'
        );
      }
    }
  };

  const handleEditNewsletter = (newsletter) => {
    navigate(`/admin/blog-editor/${newsletter.id}`);
  };

  const resetForm = () => {
    setFormData({
      newsletter_id: "",
      title: "",
      month: "",
      year: "",
      excerpt: "",
      content: "",
      author: "Admin Team",
      read_time: "5 min read",
      featured: false,
      category: "Business",
      publish_date: "",
      status: "draft"
    });
  };

  const openEditForm = (newsletter) => {
    setEditingNewsletter(newsletter);
    setFormData({
      newsletter_id: newsletter.newsletter_id,
      title: newsletter.title,
      month: newsletter.month,
      year: newsletter.year,
      excerpt: newsletter.excerpt,
      content: newsletter.content,
      author: newsletter.author,
      read_time: newsletter.read_time,
      featured: newsletter.featured,
      category: newsletter.category,
      publish_date: newsletter.publish_date,
      status: newsletter.status
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-gray-100 text-gray-800",
      published: "bg-green-100 text-green-800",
      archived: "bg-red-100 text-red-800"
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Resources Management</h2>
            <p className="text-gray-600 mt-1">Manage newsletters, blogs, videos, guides, and announcements</p>
          </div>
          <button
            onClick={handleCreateNewsletter}
            className="bg-green-600 hover:from-green-700 hover:to-green-800 text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
          >
            <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Create Resource
          </button>
        </div>
      </div>

      {/* Error Message */}
      {fetchError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-red-700">{fetchError?.data?.message || "Failed to fetch newsletters"}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Search Bar */}
      <div className="mt-6 mb-4">
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="h-5 w-5 text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Search newsletters by title, month, year, author, category..."
          />
          {searchTerm && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                onClick={clearSearch}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                title="Clear search"
              >
                <svg
                  className="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
        {searchTerm && (
          <div className="mt-2 text-sm text-gray-600">
            Found {totalItems} result{totalItems !== 1 ? "s" : ""} for "{searchTerm}"
            {totalItems === 0 && (
              <span className="text-gray-500 ml-2">
                - Try different keywords or{" "}
                <button
                  onClick={clearSearch}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  clear search
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Newsletters Table */}
      {!loading && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              Newsletters ({totalItems})
            </h3>
            <div className="flex items-center space-x-4">
              <label className="text-sm text-gray-700">
                Show:
                <select
                  value={itemsPerPage}
                  onChange={(e) =>
                    handleItemsPerPageChange(Number(e.target.value))
                  }
                  className="ml-2 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
              <span className="text-sm text-gray-700">
                Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems}
              </span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Newsletter
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Featured
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentNewsletters.map((newsletter) => (
                  <tr key={newsletter.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{newsletter.title}</div>
                        <div className="text-sm text-gray-500">{newsletter.month} {newsletter.year}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(newsletter.publish_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(newsletter.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {newsletter.featured ? (
                        <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEditNewsletter(newsletter)}
                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteNewsletter(newsletter.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && (!newsletters || newsletters.length === 0) && !searchTerm && (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-lg font-medium">No newsletters found</p>
                        <p className="text-sm mt-1">Create your first resource to get started</p>
                      </div>
                    </td>
                  </tr>
                )}
                {searchTerm && filteredNewsletters.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <p className="text-lg font-medium">No newsletters found</p>
                        <p className="text-sm mt-1">Try adjusting your search terms</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
                    <span className="font-medium">
                      {Math.min(endIndex, totalItems)}
                    </span>{" "}
                    of <span className="font-medium">{totalItems}</span> results
                  </p>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg
                        className="h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>

                    {/* Page Numbers */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((page) => {
                        // Show first page, last page, current page, and pages around current page
                        return (
                          page === 1 ||
                          page === totalPages ||
                          Math.abs(page - currentPage) <= 2
                        );
                      })
                      .map((page, index, array) => {
                        // Add ellipsis if there's a gap
                        const showEllipsis =
                          index > 0 && array[index - 1] < page - 1;
                        return (
                          <React.Fragment key={page}>
                            {showEllipsis && (
                              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                ...
                              </span>
                            )}
                            <button
                              onClick={() => handlePageChange(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === page
                                  ? "z-10 bg-blue-50 border-blue-600 text-blue-600"
                                  : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                              }`}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        );
                      })}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg
                        className="h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsletterManagement;
